import { questionIdsToHack } from "./constants";
import { QuestionHiding } from "./types";
import QuestionnaireQuestionDto from "../../../models/Request/InitialConversationQuestionDataItem";

/**
 * Get IDs of all dependent questions that should be hidden based on the parent question's answer
 * @param questionId - The ID of the parent question that changed
 * @param answerId - The new answer ID for the parent question
 * @param items - All current question items
 * @param dependencies - The question dependency configurations
 * @returns A set of question IDs that should be hidden
 */
export const getDependentQuestionsToHide = (
  questionId: number,
  answerId: number | undefined,
  items: QuestionnaireQuestionDto[],
  dependencies: QuestionHiding[] = questionIdsToHack
): Set<number> => {
  const questionIdsToHide = new Set<number>();

  // Find the dependency rule for this question
  const dependencyRule = dependencies.find(
    (rule) => rule.questionGroupQuestionId === questionId
  );

  if (!dependencyRule) {
    return questionIdsToHide;
  }

  // If the answer doesn't match the "show" condition, find direct children
  if (answerId !== dependencyRule.answerIdToShow) {
    // Add direct child questions to hide
    items.forEach((item) => {
      if (item.parentQuestionGroupQuestionId === questionId) {
        questionIdsToHide.add(item.questionGroupQuestionId);

        // Recursively add grandchildren to hide
        const grandchildren = getDependentQuestionsToHide(
          item.questionGroupQuestionId,
          item.answerId,
          items,
          dependencies
        );
        grandchildren.forEach((id) => questionIdsToHide.add(id));
      }
    });
  }

  return questionIdsToHide;
};

/**
 * Get question items with hidden questions filtered out
 * @param items - All current question items
 * @param changedQuestionId - The ID of the question that changed
 * @param changedAnswerId - The new answer for the changed question
 * @param dependencies - The question dependency configurations
 * @returns Filtered question items with hidden questions removed
 */
export const getFilteredQuestionItems = (
  items: QuestionnaireQuestionDto[],
  changedQuestionId: number,
  changedAnswerId: number | undefined,
  dependencies: QuestionHiding[] = questionIdsToHack
): QuestionnaireQuestionDto[] => {
  const questionsToHide = getDependentQuestionsToHide(
    changedQuestionId,
    changedAnswerId,
    items,
    dependencies
  );

  return items.filter(
    (item) => !questionsToHide.has(item.questionGroupQuestionId)
  );
};
